/**
 * ч.1383 — «Податковий статус за датами» в картці організації (вкладка «Основне»).
 *
 * Власник: «організація посеред року чи з початку року змінює систему оподаткування, стає
 * платником ПДВ чи перестає — треба фіксувати дати». Рядок = статус з дати до наступного рядка
 * (як облікова політика в 1С). Поля «Система оподаткування» / «Платник ПДВ» / «ІПН» над
 * таблицею — це рядок, що діє сьогодні; зміну «з дати» додають тут.
 *
 * ч.1384 (рішення власника): збережені рядки не редагуються — лише нові, ще не збережені.
 * Видалити можна рядок, що діє сьогодні, або запланований; минулі лишаються (сервер перевіряє
 * те саме). Помилку в статусі виправляють так: видалити рядок, що діє, і додати новий.
 */
import type { OrganizationCreate, OrgTaxStatus } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import TchTable from '@/shared/components/TchTable';
import { vatFixedFor } from '@/shared/components/VatPayerSelect';
import { signalUserEdit } from '@/store/formDrafts';

type TaxSystem = OrganizationCreate['tax_system'];

export const TAX_SYSTEM_OPTIONS: Record<'LEGAL_ENTITY' | 'FOP' | 'INDIVIDUAL', [TaxSystem, string][]> = {
  // Підписи — як у полі «Система оподаткування» картки.
  LEGAL_ENTITY: [
    ['GENERAL', 'Загальна (податок на прибуток 18%)'],
    ['SIMPLIFIED_3', 'Спрощена 3 група (ТОВ)'],
    ['SIMPLIFIED_4', 'Спрощена 4 (с/г ТОВ)'],
  ],
  FOP: [
    ['FOP_GENERAL', 'ФОП на загальній (ст. 177 ПКУ)'],
    ['FOP_1', 'ФОП 1 група (єд. податок)'],
    ['FOP_2', 'ФОП 2 група (єд. податок)'],
    ['FOP_3_3', 'ФОП 3 група 3% (платник ПДВ)'],
    ['FOP_3_5', 'ФОП 3 група 5% (неплатник ПДВ)'],
  ],
  // ч.1401: фізична особа у режимі «лише ЕДО» — обліку немає.
  INDIVIDUAL: [['NONE', 'Облік не ведеться (лише ЕДО)']],
};

const SINGLE_TAX = new Set<string>(['SIMPLIFIED_3', 'SIMPLIFIED_4', 'FOP_1', 'FOP_2', 'FOP_3_3', 'FOP_3_5']);

export function kyivToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Kyiv' }).format(new Date());
}

/** Індекс рядка, що діє на дату (раніше за перший рядок — перший). */
export function currentIndex(rows: OrgTaxStatus[], date: string = kyivToday()): number {
  let idx = 0;
  rows.forEach((r, i) => { if (r.valid_from <= date) idx = i; });
  return idx;
}

export const sortRows = (rows: OrgTaxStatus[]): OrgTaxStatus[] =>
  [...rows].sort((a, b) => a.valid_from.localeCompare(b.valid_from));

function nextQuarterStart(today: string): string {
  const [y, m] = today.split('-').map(Number) as [number, number];
  const q = Math.floor((m - 1) / 3) + 1;
  return q === 4 ? `${y + 1}-01-01` : `${y}-${String(3 * q + 1).padStart(2, '0')}-01`;
}

const fmt = (d: string) => d.split('-').reverse().join('.');

/** Підказки до рядків: зміна системи єдинника не з першого дня кварталу, майбутня зміна. */
export function rowHints(rows: OrgTaxStatus[], today: string = kyivToday()): string[] {
  return rows.map((r, i) => {
    const prev = rows[i - 1];
    const hints: string[] = [];
    if (prev && prev.tax_system !== r.tax_system
        && (SINGLE_TAX.has(prev.tax_system) || SINGLE_TAX.has(r.tax_system))
        && !/-(01|04|07|10)-01$/.test(r.valid_from)) {
      hints.push('систему (групу, ставку) єдинника змінюють з першого дня кварталу');
    }
    // ч.1384: день анулювання — останній день платника (ще складають ПН, зокрема на залишки).
    if (prev?.is_vat_payer && !r.is_vat_payer) {
      hints.push('«ні» — з дня, наступного за датою анулювання реєстрації');
    }
    if (r.valid_from > today) hints.push(`набуде чинності ${fmt(r.valid_from)}`);
    return hints.join('; ');
  });
}

/** Дата рядка, що діяв сьогодні серед ЗБЕРЕЖЕНИХ (раніше нього рядки не видаляють). */
export function lockedBeforeOf(saved: OrgTaxStatus[], today: string = kyivToday()): string | null {
  const rows = sortRows(saved.filter((r) => r.id));
  return rows.length ? rows[currentIndex(rows, today)]!.valid_from : null;
}

/** Чи можна прибрати рядок: новий — завжди; збережений — лише той, що діє, або запланований. */
export function canRemove(r: OrgTaxStatus, lockedBefore: string | null): boolean {
  return !r.id || lockedBefore === null || r.valid_from >= lockedBefore;
}

/** Чого бракує в історії (порожньо — усе гаразд). */
export function historyProblem(rows: OrgTaxStatus[]): string | null {
  if (!rows.length) return 'рядок у таблиці «Податковий статус за датами»';
  const seen = new Set<string>();
  for (const r of rows) {
    if (!r.valid_from) return 'дата в таблиці «Податковий статус за датами»';
    if (seen.has(r.valid_from)) return `другий рядок на ${fmt(r.valid_from)} у «Податковий статус за датами»`;
    seen.add(r.valid_from);
    if (r.is_vat_payer && r.source !== '1c' && !(r.vat_number ?? '').trim()) {
      return `ІПН платника ПДВ у рядку з ${fmt(r.valid_from)} («Податковий статус за датами»)`;
    }
  }
  return null;
}

const yesNo = (v: boolean) => (v ? 'так' : 'ні');

export default function OrgTaxHistoryTable(props: {
  rows: OrgTaxStatus[];
  entityType: 'LEGAL_ENTITY' | 'FOP' | 'INDIVIDUAL';
  onChange: (rows: OrgTaxStatus[]) => void;
  /** Дата рядка, що діяв сьогодні, серед збережених (lockedBeforeOf при завантаженні картки). */
  lockedBefore: string | null;
}) {
  const { rows, entityType, onChange, lockedBefore } = props;
  const today = kyivToday();
  const cur = currentIndex(rows, today);
  const hints = rowHints(rows, today);
  const options = TAX_SYSTEM_OPTIONS[entityType];
  const set = (next: OrgTaxStatus[]) => { signalUserEdit(); onChange(next); };
  const patch = (i: number, p: Partial<OrgTaxStatus>) =>
    set(rows.map((r, j) => (j === i ? { ...r, ...p, source: 'card' } : r)));

  const addRow = () => {
    const last = rows[rows.length - 1];
    const d = nextQuarterStart(today);
    const valid_from = rows.some((r) => r.valid_from === d) ? today : d;
    set(sortRows([...rows, {
      id: null, valid_from, tax_system: last?.tax_system ?? options[0]![0],
      is_vat_payer: last?.is_vat_payer ?? false, vat_number: last?.vat_number ?? null, source: 'card',
    }]));
  };

  return (
    <div style={{ marginTop: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
        <strong>Податковий статус за датами</strong>
        <small style={{ color: 'var(--muted)' }}>
          Статус діє з дати до наступного рядка. Декларації, єдиний податок і ПДВ у документах
          рахуються за статусом свого періоду. Збережені рядки не змінюються: зміну статусу
          додайте новим рядком; помилку виправте так — видаліть рядок, що діє, і додайте новий.
        </small>
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 130 }}>З дати</th>
            <th style={{ width: 230 }}>Система оподаткування</th>
            <th style={{ width: 110 }}>Платник ПДВ</th>
            <th style={{ width: 150 }}>ІПН платника ПДВ</th>
            <th>Примітка</th>
            <th style={{ width: 36 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const fixed = vatFixedFor(r.tax_system);
            const saved = !!r.id;
            const removable = canRemove(r, lockedBefore);
            const label = options.find(([v]) => v === r.tax_system)?.[1] ?? r.tax_system;
            return (
              <tr key={r.id ?? `new-${i}`}
                style={i === cur ? { background: 'var(--badge-green-bg)' } : undefined}>
                <td>
                  {saved ? fmt(r.valid_from) : (
                    <DatePopover value={r.valid_from}
                      onChange={(v) => v && set(sortRows(rows.map((x, j) => (j === i
                        ? { ...x, valid_from: v, source: 'card' } : x))))} />
                  )}
                </td>
                <td>
                  {saved ? label : (
                    <select value={r.tax_system}
                      onChange={(e) => {
                        const ts = e.target.value as TaxSystem;
                        const f = vatFixedFor(ts);
                        patch(i, { tax_system: ts, is_vat_payer: f ?? r.is_vat_payer,
                          vat_number: (f ?? r.is_vat_payer) ? r.vat_number : null });
                      }}>
                      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      {!options.some(([v]) => v === r.tax_system) && (
                        <option value={r.tax_system}>{r.tax_system}</option>
                      )}
                    </select>
                  )}
                </td>
                <td>
                  {saved ? yesNo(r.is_vat_payer) : (
                    <select value={r.is_vat_payer ? 'yes' : 'no'} disabled={fixed !== null}
                      title={fixed !== null ? 'Визначається системою оподаткування' : undefined}
                      onChange={(e) => {
                        const v = e.target.value === 'yes';
                        patch(i, { is_vat_payer: v, vat_number: v ? r.vat_number : null });
                      }}>
                      <option value="yes">так</option>
                      <option value="no">ні</option>
                    </select>
                  )}
                </td>
                <td>
                  {!r.is_vat_payer ? <span style={{ color: 'var(--muted)' }}>—</span>
                    : saved ? (r.vat_number || <span style={{ color: 'var(--muted)' }}>з 1С без ІПН</span>) : (
                      <input value={r.vat_number ?? ''} maxLength={12} inputMode="numeric"
                        aria-invalid={!(r.vat_number ?? '').trim() || undefined}
                        placeholder="9–12 цифр"
                        onChange={(e) => patch(i, { vat_number: e.target.value.replace(/\D/g, '') || null })} />
                    )}
                </td>
                <td>
                  <small style={{ color: 'var(--muted)' }}>
                    {[i === cur ? 'діє зараз' : null, saved ? null : 'новий — збережіть картку',
                      r.source === '1c' ? 'з облікової політики 1С' : null,
                      hints[i] || null].filter(Boolean).join('; ')}
                  </small>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button type="button" className="danger" disabled={!removable}
                    title={removable ? 'Прибрати рядок'
                      : 'Минулий рядок не видаляють — видалити можна рядок, що діє, або запланований'}
                    aria-label={`Прибрати рядок з ${fmt(r.valid_from)}`}
                    onClick={() => set(rows.filter((_, j) => j !== i))}>×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </TchTable>
      <button type="button" style={{ marginTop: '0.35rem' }} onClick={addRow}>
        + додати зміну статусу
      </button>
    </div>
  );
}
